import React, { useMemo } from 'react';

import { Box, Button, Typography } from '@material-ui/core';

import { ConfirmDialog, GridCellWithAvatar } from '@vyce/core/src/components';
import { AppSearchInput } from '@vyce/core/src/components/inputs/AppSearchInput';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { getAvatar } from '@vyce/core/src/utils/getAvatar';
import { getUKFormattedDate } from '@vyce/core/src/utils/dates';
import { WidgetWrapper, WidgetWrapperProps } from '@vyce/core/src/components/Dashboard/WidgetWrapper';
import { DormantWorkerDTO } from '@vyce/core/src/api/types';

import { useWidgetData } from './hooks';
import { useStyles } from './styles';
import { Props } from './types';
import { EnrichedColumnProps } from '../../../components/InfiniteTable/types';
import { InfiniteTable } from '../../../components/InfiniteTable';

/* Team members on T&A that haven't clocked in within the last 4 weeks */

export const DormantTeamMembersWidget = (props: Props & WidgetWrapperProps) => {
  const classes = useStyles();
  const {
    workers,
    total,
    loading,
    isMobile,
    sortModel,
    isLoadingList,
    isConfirmDialogOpen,
    isRemoving,
    workerToRemove,
    resetLoadingList,
    getDormantWorkers,
    handleSortModelChange,
    handleSearchChange,
    handleRemoveClick,
    handleCloseConfirmDialog,
    handleConfirmRemove,
  } = useWidgetData(props);

  const columns: EnrichedColumnProps<DormantWorkerDTO>[] = useMemo(
    () => [
      {
        field: 'first_name',
        headerName: 'Name',
        flex: 1,
        width: 150,
        sortable: true,
        renderCell: row => (
          <GridCellWithAvatar
            avatarUrl={row.photo_url}
            avatarPlaceholder={getAvatar()}
            link={`/time/users/${row.first_name}_${row.user_id}/logs`}
            name={`${row.first_name} ${row.last_name}`}
            target="_blank"
          />
        ),
      },
      {
        field: 'last_clock_in_date',
        headerName: 'Last Clock In Date',
        flex: 0.8,
        width: 140,
        sortable: true,
        valueGetter: row => getUKFormattedDate(row.last_clock_in_date),
      },
      {
        field: 'last_clock_in_site_name',
        headerName: 'Last Clock In Location',
        flex: 0.8,
        width: 140,
        sortable: true,
      },
      {
        field: 'customCell',
        headerName: 'Remove from all Locations',
        flex: 0.8,
        width: 160,
        sortable: false,
        renderCell: row => (
          <Box width="100%" display="flex" alignItems="center">
            <Button
              className={classes.removeButton}
              size="small"
              color="primary"
              variant="contained"
              onClick={() => handleRemoveClick(row)}>
              Remove
            </Button>
          </Box>
        ),
      },
    ],
    [classes.removeButton, handleRemoveClick]
  );

  return (
    <WidgetWrapper {...props}>
      <Box width="100%" height="100%" display="flex" flexDirection="column" gridGap={16}>
        <Box display="flex" alignItems="center" gridGap={16}>
          <Typography color="primary" className={classes.count}>
            {total} workers
          </Typography>
          <Box width="200px">
            <AppSearchInput onChange={handleSearchChange} isSmall isBorder expanded={!isMobile} />
          </Box>
        </Box>

        <InfiniteTable
          isLoading={loading}
          data={workers}
          columns={columns}
          rowCount={total}
          rowHeight={54}
          height="calc(100% - 1px)"
          pageSize={GRID_PAGE_SIZE}
          isLoadingList={isLoadingList}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onLoadMore={getDormantWorkers}
          resetLoadingList={resetLoadingList}
        />
      </Box>

      <ConfirmDialog
        open={isConfirmDialogOpen}
        loading={isRemoving}
        title="Remove team member"
        subtitle={
          workerToRemove
            ? `Remove ${workerToRemove.first_name} ${workerToRemove.last_name} from all locations? This clears all of their Time & Attendance data.`
            : ''
        }
        confirmText="Remove"
        handleClose={handleCloseConfirmDialog}
        handleConfirm={handleConfirmRemove}
      />
    </WidgetWrapper>
  );
};
